
import { getWindowWidth } from '../base/environment/utils';
import { toState } from '../base/redux';
import { CHAT_SIZE } from '../chat/constants';
import { isFixedCaptionsVisible } from '../subtitles/functions.web';

import {
    CURRENT_TILE_VIEW_PAGE,
    HIDE_LOCAL_PARTICIPANT_IN_STAGE_VIEW,
    SET_HIDE_HEADER_FOOTER,
    SET_HORIZONTAL_VIEW_DIMENSIONS,
    SET_TILE_VIEW_DIMENSIONS,
    SET_VISIBLE_PARTICIPANTS_START_INDEX,
    SET_VISIBLE_REMOTE_PARTICIPANTS,
    TILE_VIEW_TILE_LIMIT
} from './actionTypes';
import {
    calculateThumbnailSizeForHorizontalView,
    calculateThumbnailSizeForTileView
} from './functions';

/**
 * The size of the side margins for the entire tile view area.
 */
const TILE_VIEW_SIDE_MARGINS = 20;

/**
 * Height taken from the tile grid by the docked captions panel (panel + gap).
 * shortcut: matches the default .captions-fixed-container.fixed-view height,
 * measure the element if its responsive heights start to matter.
 */
const FIXED_CAPTIONS_HEIGHT = 210;

/**
 * Sets the dimensions of the tile view grid.
 *
 * @param {Object} dimensions - Whether the filmstrip is visible.
 * @param {Object} windowSize - The size of the window.
 * @param {Object | Function} stateful - An object or function that can be
 * resolved to Redux state using the {@code toState} function.
 * @returns {{
 *     type: SET_TILE_VIEW_DIMENSIONS,
 *     dimensions: Object
 * }}
 */
export function setTileViewDimensions(
        dimensions: Object,
        windowSize: Object,
        stateful: Object | Function
) {
    const state = toState(stateful);
    const { clientWidth, clientHeight } = windowSize;
    const heightToUse = isFixedCaptionsVisible(state)
        ? Math.max(clientHeight - FIXED_CAPTIONS_HEIGHT, 0)
        : clientHeight;
    let widthToUse = clientWidth;
    const { isOpen } = state['features/chat'];
    const { disableResponsiveTiles } = state['features/base/config'];
    const { isOpen: partOpen } = state['features/participants-pane'];

    if (
        isOpen
    && (clientWidth === getWindowWidth()
      || clientWidth === getWindowWidth() - 315)
    ) {
        widthToUse -= CHAT_SIZE;
    }

    if (!isOpen && clientWidth !== getWindowWidth() && !partOpen) {
        widthToUse = getWindowWidth();
    }

    const thumbnailSize = calculateThumbnailSizeForTileView({
        ...dimensions,
        clientWidth: widthToUse,
        clientHeight: heightToUse,
        disableResponsiveTiles
    });
    const filmstripWidth
    = dimensions.columns * (TILE_VIEW_SIDE_MARGINS + thumbnailSize.width);

    return {
        type: SET_TILE_VIEW_DIMENSIONS,
        dimensions: {
            gridDimensions: dimensions,
            thumbnailSize,
            filmstripWidth
        }
    };
}

/**
 * Sets the dimensions of the thumbnails in horizontal view.
 *
 * @param {number} clientHeight - The height of the window.
 * @returns {{
 *     type: SET_HORIZONTAL_VIEW_DIMENSIONS,
 *     dimensions: Object
 * }}
 */
export function setHorizontalViewDimensions(clientHeight: number = 0) {
    return {
        type: SET_HORIZONTAL_VIEW_DIMENSIONS,
        dimensions: calculateThumbnailSizeForHorizontalView(clientHeight)
    };
}

/**
 * Sets the list of the visible participants in the filmstrip by storing the start and end index from the remote
 * participants array.
 *
 * @param {number} startIndex - The start index from the remote participants array.
 * @param {number} endIndex - The end index from the remote participants array.
 * @returns {{
 *      type: SET_VISIBLE_REMOTE_PARTICIPANTS,
 *      startIndex: number,
 *      endIndex: number
 * }}
 */
export function setVisibleRemoteParticipants(
        startIndex: number,
        endIndex: number
) {
    return {
        type: SET_VISIBLE_REMOTE_PARTICIPANTS,
        startIndex,
        endIndex
    };
}

/**
 * Sets the list of the visible participants in the filmstrip by storing the start and end index from the remote
 * participants array.
 *
 * @param {number} volum - The start index from the remote participants array.
 * @returns {{
 *      type: SET_VISIBLE_REMOTE_PARTICIPANTS,
 *      startIndex: number,
 *      endIndex: number
 * }}
 */
export function onVolumeChange(volum: ?number) {
    return {
        type: SET_VISIBLE_REMOTE_PARTICIPANTS,
        volum
    };
}

/** * Sets the tile limit for the tile view.
 *
 * @param {number} tileLimit - The maximum number of tiles to display in the tile view.
 * @returns {{
 *     type: TILE_VIEW_TILE_LIMIT,
 *     tileViewTileLimit: number
 * }}
 */
export function setTileLimitTileView(tileViewTileLimit: number) {
    return {
        type: TILE_VIEW_TILE_LIMIT,
        tileViewTileLimit
    };
}

/** * hide the local participant in stage view .
 *
 * @param {Boolean} hidelocal - true/false to hide local participant in stage view.
 * @returns {{
 *     type: HIDE_LOCAL_PARTICIPANT_IN_STAGE_VIEW,
 *     hideLocal: Boolean
 * }}
 */
export function hideLocalParticipantInStageView(hidelocal: Boolean) {
    return {
        type: HIDE_LOCAL_PARTICIPANT_IN_STAGE_VIEW,
        hidelocal
    };
}

/** * hide the header and footer in filmstrip .
 *
 * @param {boolean} hideHeaderFooter - true/false to hide header and footer.
 * @returns {{
 *     type: SET_HIDE_HEADER_FOOTER,
 *     hideHeaderFooter: boolean
 * }}
 */
export function setHideHeaderFooter(hideHeaderFooter: boolean) {
    return {
        type: SET_HIDE_HEADER_FOOTER,
        hideHeaderFooter
    };
}

/**
 * Sets the start index for visible participants in tile view carousel.
 *
 * @param {number} startIndex - The start index for visible participants.
 * @returns {{
 *     type: SET_VISIBLE_PARTICIPANTS_START_INDEX,
 *     startIndex: number
 * }}
 */
export function setVisibleParticipantsStartIndex(startIndex: number) {
    return {
        type: SET_VISIBLE_PARTICIPANTS_START_INDEX,
        startIndex
    };
}

/**
 * Sets the current page for tile view carousel.
 *
 * @param {number} page - The current page for tile view carousel.
 * @returns {{
 *     type: CURRENT_TILE_VIEW_PAGE,
 *     page: number
 * }}
 */
export function setCurrentTileViewPage(page: number) {
    return {
        type: CURRENT_TILE_VIEW_PAGE,
        page
    };
}


export * from './actions.native';


